/* ==========================================================================
   NEURODAZZLED V2 — nd-base.js (ES module, ~4 KB)
   Noise On/Off · Resolve · mobile menu · cart count · purchase bar · review panel
   No dependencies. Everything degrades to a working page without JavaScript.
   ========================================================================== */
const html = document.documentElement;
const strings = (window.nd && window.nd.strings) || {};
const live = () => document.querySelector('[data-nd-live]');
const announce = (msg) => { const el = live(); if (el) { el.textContent = ''; setTimeout(() => { el.textContent = msg; }, 30); } };

/* ---------- Noise On / Noise Off ---------------------------------------- */
function setNoise(state, persist = true, speak = false) {
  html.setAttribute('data-noise', state);
  if (persist) { try { localStorage.setItem('nd-noise', state); } catch (e) {} }
  document.querySelectorAll('[data-nd-noise-toggle]').forEach((btn) => {
    btn.setAttribute('aria-pressed', state === 'on' ? 'true' : 'false');
    const label = btn.querySelector('[data-nd-noise-state]');
    if (label) label.textContent = state === 'on' ? (strings.noiseOn || 'on') : (strings.noiseOff || 'off');
  });
  if (state === 'off') resolveAll();
  if (speak) announce(state === 'on' ? (strings.noiseOnAnnounce || 'Noise on') : (strings.noiseOffAnnounce || 'Noise off'));
  document.dispatchEvent(new CustomEvent('nd:noise', { detail: { state } }));
  syncReview();
}
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-nd-noise-toggle]');
  if (!btn) return;
  setNoise(html.getAttribute('data-noise') === 'on' ? 'off' : 'on', true, true);
});
setNoise(html.getAttribute('data-noise') || 'on', false);

/* ---------- Motion preference (OS) -------------------------------------- */
const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
mq.addEventListener && mq.addEventListener('change', () => {
  let forced = null; try { forced = sessionStorage.getItem('nd-review-motion'); } catch (e) {}
  if (!forced) html.setAttribute('data-motion', mq.matches ? 'reduce' : 'ok');
  if (mq.matches) resolveAll();
});

/* ---------- Resolve: noisy → defined, once, on entering the viewport ---- */
let io = null;
function resolveAll() { document.querySelectorAll('[data-nd-resolve], [data-nd-reveal], .nd-static').forEach((el) => el.classList.add('is-resolved')); }
function initResolve(root = document) {
  const els = root.querySelectorAll('[data-nd-resolve]:not(.is-resolved), [data-nd-reveal]:not(.is-resolved), .nd-static:not(.is-resolved)');
  if (!('IntersectionObserver' in window) || html.getAttribute('data-motion') !== 'ok' || html.getAttribute('data-noise') !== 'on') {
    els.forEach((el) => el.classList.add('is-resolved'));
    return;
  }
  io = io || new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) { entry.target.classList.add('is-resolved'); io.unobserve(entry.target); }
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
  html.classList.add('nd-resolve-on');
  els.forEach((el) => {
    // Anything already on screen or scrolled past resolves immediately — nothing is ever left hidden.
    // (On-screen elements get one frame of the 'before' state so the transition can play.)
    if (el.getBoundingClientRect().top < window.innerHeight * 0.95) requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('is-resolved')));
    else io.observe(el);
  });
}
let ndSweepT = null;
window.addEventListener('scroll', () => {
  if (ndSweepT) return;
  ndSweepT = setTimeout(() => {
    ndSweepT = null;
    const vh = window.innerHeight;
    document.querySelectorAll('[data-nd-resolve]:not(.is-resolved), [data-nd-reveal]:not(.is-resolved), .nd-static:not(.is-resolved)').forEach((el) => {
      if (el.getBoundingClientRect().top < vh * 0.95) el.classList.add('is-resolved');
    });
  }, 120);
}, { passive: true });
initResolve();
document.addEventListener('shopify:section:load', (e) => initResolve(e.target));
window.ndReady = true;

/* ---------- Mobile menu (native <dialog>) ------------------------------- */
document.addEventListener('click', (e) => {
  const open = e.target.closest('[data-nd-drawer-open]');
  if (open) {
    const d = document.getElementById(open.getAttribute('aria-controls'));
    if (d && typeof d.showModal === 'function') { d.showModal(); open.setAttribute('aria-expanded', 'true'); d._opener = open; }
    return;
  }
  const close = e.target.closest('[data-nd-drawer-close]');
  if (close) { const d = close.closest('dialog'); if (d) d.close(); return; }
  if (e.target.matches('dialog.nd-drawer')) e.target.close(); // backdrop click
});
document.addEventListener('close', (e) => {
  const d = e.target;
  if (d && d._opener) { d._opener.setAttribute('aria-expanded', 'false'); d._opener.focus(); }
}, true);

/* ---------- Cart count --------------------------------------------------- */
async function refreshCartCount() {
  try {
    const res = await fetch((window.nd.routes && window.nd.routes.cartJs) || '/cart.js', { headers: { Accept: 'application/json' } });
    const cart = await res.json();
    document.querySelectorAll('[data-nd-cart-count]').forEach((el) => { el.textContent = cart.item_count; el.setAttribute('data-count', cart.item_count); });
    document.querySelectorAll('[data-nd-cart-label]').forEach((el) => {
      el.setAttribute('aria-label', (el.getAttribute('data-label-template') || 'Bag (COUNT)').replace('COUNT', cart.item_count));
    });
  } catch (e) {}
}
document.addEventListener('nd:cart-updated', refreshCartCount);

/* ---------- Mobile purchase bar ----------------------------------------- */
const buybar = document.querySelector('[data-nd-buybar]');
const buyAnchor = document.querySelector('[data-nd-buy-anchor]');
if (buybar && buyAnchor) {
  document.body.classList.add('nd-has-buybar');
  let shown = null;
  const setBar = () => {
    // Show only once the main Add to bag button has scrolled up out of view.
    const show = buyAnchor.getBoundingClientRect().bottom < 0;
    if (show === shown) return;
    shown = show;
    buybar.classList.toggle('is-visible', show);
    buybar.toggleAttribute('inert', !show);
    buybar.setAttribute('aria-hidden', show ? 'false' : 'true');
  };
  let t = null;
  window.addEventListener('scroll', () => { if (!t) t = setTimeout(() => { t = null; setBar(); }, 100); }, { passive: true });
  setBar();
}

/* ---------- V2 review panel --------------------------------------------- */
function syncReview() {
  const panel = document.querySelector('[data-nd-review]');
  if (!panel) return;
  const sig = html.getAttribute('data-signal') || 'red';
  panel.querySelectorAll('[data-nd-review-signal]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.ndReviewSignal === sig)));
  panel.querySelectorAll('[data-nd-review-noise]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.ndReviewNoise === html.getAttribute('data-noise'))));
  panel.querySelectorAll('[data-nd-review-motion]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.ndReviewMotion === html.getAttribute('data-motion'))));
}
document.addEventListener('click', (e) => {
  const s = e.target.closest('[data-nd-review-signal]');
  if (s) { html.setAttribute('data-signal', s.dataset.ndReviewSignal); try { sessionStorage.setItem('nd-review-signal', s.dataset.ndReviewSignal); } catch (x) {} syncReview(); return; }
  const n = e.target.closest('[data-nd-review-noise]');
  if (n) { setNoise(n.dataset.ndReviewNoise, true, true); return; }
  const m = e.target.closest('[data-nd-review-motion]');
  if (m) {
    const v = m.dataset.ndReviewMotion;
    html.setAttribute('data-motion', v);
    try { sessionStorage.setItem('nd-review-motion', v); } catch (x) {}
    if (v === 'reduce') resolveAll(); else { document.querySelectorAll('[data-nd-resolve], [data-nd-reveal]').forEach((el) => el.classList.remove('is-resolved')); requestAnimationFrame(() => initResolve()); }
    document.dispatchEvent(new CustomEvent('nd:motion', { detail: { state: v } }));
    syncReview();
  }
});
syncReview();
